/**
 * 分形索引（fractional indexing）。
 * 借鉴 AFFiNE LayerManager 的做法：用有序字符串表示 z 序，
 * 插入 / 重排无需整体重编号。
 */

const DIGITS =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const BASE = DIGITS.length;
const MID = Math.floor(BASE / 2); // 'V'

function digit(ch: string): number {
  const index = DIGITS.indexOf(ch);
  return index === -1 ? 0 : index;
}

/** 返回严格大于 a 的键。 */
function increment(a: string): string {
  return a + DIGITS[MID];
}

/** 返回严格小于 b 的键。 */
function decrement(b: string): string {
  const d = digit(b[0] ?? DIGITS[MID]);
  if (d > 1) return DIGITS[d - 1];
  return DIGITS[0] + DIGITS[MID];
}

/** 返回位于 a、b 之间的键。 */
function midpoint(a: string, b: string): string {
  let n = 0;
  while (n < a.length && n < b.length && a[n] === b[n]) n++;
  if (n > 0) return a.slice(0, n) + midpoint(a.slice(n), b.slice(n));

  const da = a.length ? digit(a[0]) : 0;
  const db = b.length ? digit(b[0]) : BASE;
  if (db - da > 1) {
    return DIGITS[Math.round((da + db) / 2)];
  }
  if (b.length > 1) return b.slice(0, 1);
  if (a.length > 0) return DIGITS[da] + midpoint(a.slice(1), '');
  return DIGITS[Math.max(1, da)];
}

/**
 * 生成位于 a 与 b 之间的排序键。
 * 传入 null 表示无界。要求 a < b（若都提供）。
 */
export function generateKeyBetween(
  a: string | null,
  b: string | null
): string {
  if (a && b) {
    if (a >= b) return increment(a);
    return midpoint(a, b);
  }
  if (a) return increment(a);
  if (b) return decrement(b);
  return String(DIGITS[MID]);
}

/** 批量生成 n 个位于 (a, b) 之间的键。 */
export function generateKeysBetween(
  a: string | null,
  b: string | null,
  n: number
): string[] {
  const keys: string[] = [];
  let lower = a;
  for (let i = 0; i < n; i++) {
    const key = generateKeyBetween(lower, b);
    keys.push(key);
    lower = key;
    if (b && key >= b) break;
  }
  return keys;
}

export function generateNKeysBetween(
  a: string | null,
  b: string | null,
  n: number
): string[] {
  if (n === 0) return [];
  if (n === 1) return [generateKeyBetween(a, b)];
  const mid = Math.floor(n / 2);
  const midKey = generateKeyBetween(a, b);
  return [
    ...generateNKeysBetween(a, midKey, mid),
    midKey,
    ...generateNKeysBetween(midKey, b, n - mid - 1),
  ];
}

/** 用于插入到最前 / 最后。 */
export const Z_FIRST = generateKeyBetween(null, null);
export function zBefore(first: string | null): string {
  return generateKeyBetween(null, first);
}
export function zAfter(last: string | null): string {
  return generateKeyBetween(last, null);
}